import React, { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AppRoutes from "../AppRoutes";
import moment from "moment/min/moment-with-locales";

import { getLangFromPathname } from "../utils/i18nRoute";

/**
 * LangWrapper
 * Source of truth: URL
 * - EN: /en/...
 * - FR: /...
 */
function LangWrapper(props) {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const { defaultLang, ...rest } = props;
  const languagedata = useSelector((state) => state.languagedata);
  const location = useLocation();

  const resolveLanguage = () => {
    // 1. Pathname if /en
    const pathLang = getLangFromPathname(location.pathname);
    if (pathLang === "en") return "en";

    // 2. Query param ?lang= (e.g. iframe context ?lang=en / ?lang=fr)
    try {
      const searchParams = new URLSearchParams(location.search);
      const qLang = searchParams.get("lang");
      if (qLang) {
        const val = qLang.toLowerCase().trim();
        if (val.startsWith("en")) return "en";
        if (val.startsWith("fr")) return "fr";
      }
    } catch (_) {}

    return pathLang;
  };

  const resolveDateLocale = (language) => {
    // Find the specific dateLocale from Redux if possible
    let dateLocale = language;
    if (languagedata?.langlist) {
      const matching = Object.values(languagedata.langlist).find(
        (l) => l?.langLocale === language
      );
      if (matching?.dateLocale) dateLocale = matching.dateLocale;
    }
    return dateLocale;
  };

  useEffect(() => {
    const language = resolveLanguage();
    const dateLocale = resolveDateLocale(language);

    /* console.log(
      "[I18N_FIX] LangWrapper: path=",
      location.pathname,
      "-> targetLang=",
      language,
      " currentI18n=",
      i18n.language
    ); */

    // Always keep moment aligned with dateLocale
    moment.locale(dateLocale);

    // Ensure all dynamic database translations are loaded into i18n
    if (languagedata?.langlist) {
      const ns = "translations";
      const list = Object.values(languagedata.langlist).filter(Boolean);
      list.forEach((l) => {
        if (l?.langLocale && l?.keyValuePairs) {
          const loc = l.langLocale;
          const shortLoc = loc.split("-")[0].toLowerCase();
          i18n.addResourceBundle(loc, ns, l.keyValuePairs, true, true);
          if (shortLoc !== loc) {
            i18n.addResourceBundle(shortLoc, ns, l.keyValuePairs, true, true);
          }
        }
      });
    }

    // Change i18n only if needed
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }

    // Sync localStorage to prevent other bootstraps from overriding language
    try {
      const stored = localStorage.getItem("lang");
      const parsed = stored ? JSON.parse(stored) : null;
      if (!parsed || parsed.langLocale !== language || parsed.dateLocale !== dateLocale) {
        localStorage.setItem(
          "lang",
          JSON.stringify({
            langLocale: language,
            dateLocale: dateLocale,
          })
        );
      }
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, props.lang, defaultLang, location.pathname, location.search, languagedata?.langlist]);

  return <AppRoutes {...rest} />;
}

export default LangWrapper;